import React, { useState } from "react";
import { TextDescription, TextTitle } from "components/Texts";
import { Trans, useTranslation } from "react-i18next";

import { Line } from "react-chartjs-2";
import Section from "components/Section";
import _filter from "lodash/filter";
import _map from "lodash/map";
import colors from "styles/colors";
import exhaustionToolTip from "./ExhaustionToolTip";
import formatMoney from "../../modules/utils/formatMoney";
import getMax from "services/getMaxJsonArray";
import moment from "moment";
import styled from "styled-components";

const ExhaustionEstimation = ({ avgUsage, remainingBudgetList = [], daysToExhaust = "-" }) => {
  const [t] = useTranslation();
  const [tooltip, setTooltip] = useState();
  return (
    <Section margin="0">
      <Trans i18nKey="__days_to_exhust" count={daysToExhaust}>
        <TextTitle></TextTitle>
      </Trans>
      <TextDescription>{t("__estimate_on_current_budget")}</TextDescription>
      {avgUsage && daysToExhaust && remainingBudgetList.length ? (
        <BarZone>
          <Graph setTooltip={setTooltip} avgUsage={avgUsage} remainingBudgetList={remainingBudgetList} />
          {tooltip}
        </BarZone>
      ) : null}
    </Section>
  );
};

export default ExhaustionEstimation;

const Graph = React.memo(({ setTooltip, avgUsage, remainingBudgetList }) => {
  let budgetData = [];
  remainingBudgetList.forEach((item) => {
    budgetData.unshift({
      date: String("00" + moment(item.datetime).date()).slice(-2),
      budget: parseFloat(item.RemainingBudget).toFixed(2),
    });
  });
  if (remainingBudgetList.length) {
    let remainingBudget = remainingBudgetList[0].RemainingBudget;
    let calcDay = moment();
    while (remainingBudget > 0 && avgUsage > 0) {
      remainingBudget = remainingBudget - avgUsage;
      calcDay = calcDay.add(1, "days");
      budgetData.push({
        date: String("00" + calcDay.date()).slice(-2),
        budget: remainingBudget < 0 ? 0.0 : parseFloat(remainingBudget).toFixed(2),
        estimated: true,
      });
    }
  }

  const defaultStyle = {
    label: "",
    pointRadius: 0,
    pointHitRadius: 10,
    borderWidth: 2,
    borderColor: colors.primary,
    borderCapStyle: "butt",
    borderJoinStyle: "miter",
    lineTension: 0,
  };

  const spentBudgets = _map(
    _filter(budgetData, (item) => !item.estimated && item.budget),
    (item) => item.budget,
  );
  const estimatedBudgets = budgetData.map((item) => item.estimated && item.budget);
  if (spentBudgets.length > 0) {
    estimatedBudgets[spentBudgets.length - 1] = spentBudgets[spentBudgets.length - 1];
  }

  const data = (canvas) => {
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, 200);
    gradient.addColorStop(0, colors.trendingFull);
    gradient.addColorStop(0.85, colors.trendingBlank);
    return {
      labels: budgetData.map((item) => item.date),
      datasets: [
        {
          ...defaultStyle,
          fill: true,
          data: spentBudgets,
          spanGaps: false,
          backgroundColor: gradient,
        },
        {
          ...defaultStyle,
          fill: false,
          borderDash: [4, 4],
          borderDashOffset: -8,
          data: estimatedBudgets,
        },
      ],
      tooltips: {
        enabled: false,
      },
    };
  };
  return (
    <Line
      data={data}
      datasetKeyProvider={() => avgUsage}
      options={{
        layout: {
          padding: {
            top: 40,
          },
        },
        tooltips: {
          xPadding: 0,
          yPadding: 0,
          enabled: false,
          custom: (tooltipModel) => {
            exhaustionToolTip(tooltipModel, setTooltip);
          },
        },
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              ticks: {
                fontColor: colors.primary,
                fontSize: 10,
              },
            },
          ],
          yAxes: [
            {
              gridLines: {
                drawBorder: false,
                color: colors.sectionLine,
                zeroLineColor: colors.sectionLine,
              },
              ticks: {
                stepSize: parseInt(getMax(budgetData, "budget").budget / 4) + 1,
                fontColor: colors.primary,
                callback: (value) => formatMoney(value),
                fontSize: 10,
              },
            },
          ],
        },
        maintainAspectRatio: false,
      }}
    />
  );
});

const BarZone = styled.div`
  height: 200px;
  position: relative;
`;
